import { Page } from "@/types";
import { useContext, useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import { LayoutContext } from "../../admin/layout/context/layoutcontext";
import Layout from '../layout/layout';
import AppBar from '../layout/AppBar';
import { 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt, 
  FaClock, 
  FaLinkedin, 
  FaTwitter, 
  FaGithub,
  FaPaperPlane,
  FaHeadset,
  FaGlobe,
  FaUser,
  FaBuilding,
  FaCheckCircle
} from "react-icons/fa";

const ContactUs: Page = () => {
    const { layoutConfig } = useContext(LayoutContext);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        jobTitle: '',
        inquiryType: '',
        message: '',
        consent: false
    });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const inquiryTypes = [
        { label: 'Product Demo Request', value: 'demo' },
        { label: 'Sales Inquiry', value: 'sales' },
        { label: 'Technical Support', value: 'support' },
        { label: 'Partnership Opportunity', value: 'partnership' },
        { label: 'Media Inquiry', value: 'media' },
        { label: 'General Question', value: 'general' },
    ];

    const contactInfo = [
        {
            icon: <FaEnvelope />,
            title: 'Email',
            details: ['contact@amanziLytics.com', 'support@amanziLytics.com'],
            color: 'bg-blue-500'
        },
        {
            icon: <FaPhone />,
            title: 'Phone',
            details: ['+263 77 686 6462', '+263 71 686 6462'],
            color: 'bg-green-500'
        },
        {
            icon: <FaMapMarkerAlt />,
            title: 'Office',
            details: ['123 Gorge Silundika', 'Bulawayo, CA 94105', 'Zimbabwe'],
            color: 'bg-red-500'
        },
        {
            icon: <FaClock />,
            title: 'Hours',
            details: ['Monday - Friday: 8AM - 5PM', 'Saturday: 9AM - 1PM', 'Emergency Support: 24/7',],
            color: 'bg-purple-500'
        },
    ];

    const teamMembers = [
        {
            name: 'Sarah Johnson',
            role: 'Sales Director',
            email: 'sarah@amanziLytics.com',
            phone: '+1 (555) 234-5678',
            image: '👩‍💼'
        },
        {
            name: 'Michael Chen',
            role: 'Technical Lead',
            email: 'michael@amanziLytics.com',
            phone: '+1 (555) 345-6789',
            image: '👨‍💻'
        },
        {
            name: 'Alex Rodriguez',
            role: 'Customer Success',
            email: 'alex@amanziLytics.com',
            phone: '+1 (555) 456-7890',
            image: '👨‍💼'
        },
    ];

    const faqs = [
        {
            question: 'What is AmanziLytics?',
            answer: 'AmanziLytics is an AI-powered water intelligence platform that helps municipalities and water utilities optimize their water infrastructure through real-time analytics, predictive insights, and intelligent management solutions.'
        },
        {
            question: 'How long does implementation take?',
            answer: 'Implementation typically takes 4-8 weeks depending on your infrastructure complexity. We provide dedicated onboarding specialists to ensure a smooth transition.'
        },
        {
            question: 'Do you offer free trials?',
            answer: 'Yes, we offer a 30-day free trial of our platform. Contact our sales team to set up a customized demo environment.'
        },
        {
            question: 'What support options are available?',
            answer: 'We offer 24/7 technical support, dedicated account managers, comprehensive documentation, and regular training sessions.'
        },
    ];

    // Get theme-based colors
    const getThemeColors = () => {
        const isDark = layoutConfig.colorScheme === "dark" || layoutConfig.colorScheme === "dim";
        
        return {
            primaryColor: isDark ? '#0ea5e9' : '#0369a1',
            primaryLight: isDark ? '#38bdf8' : '#0284c7',
            primaryBg: isDark ? 'rgba(14, 165, 233, 0.1)' : 'rgba(3, 105, 161, 0.1)',
            successColor: isDark ? '#10b981' : '#059669',
            warningColor: isDark ? '#f59e0b' : '#d97706',
            dangerColor: isDark ? '#ef4444' : '#dc2626',
            infoColor: isDark ? '#3b82f6' : '#2563eb',
            navbarBg: isDark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            footerBg: isDark ? '#0f172a' : '#f8fafc',
            textColor: isDark ? '#f1f5f9' : '#1e293b',
            lightText: isDark ? '#94a3b8' : '#64748b',
            borderColor: isDark ? '#334155' : '#e2e8f0',
            cardBg: isDark ? '#1e293b' : '#ffffff',
            surfaceBg: isDark ? '#0f172a' : '#f1f5f9',
            hoverBg: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
        };
    };

    const colors = getThemeColors();

    const handleInputChange = (e: any) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        setLoading(false);
        setSubmitted(true);
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            company: '',
            jobTitle: '',
            inquiryType: '',
            message: '',
            consent: false
        });
    };

    const headerTemplate = () => (
        <div className="text-center mb-8">
            <span className="font-bold px-4 py-2 border-round-full text-sm inline-block mb-4"
                  style={{ 
                      backgroundColor: colors.primaryBg, 
                      color: colors.primaryColor 
                  }}>
                GET IN TOUCH
            </span>
            <h1 className="font-bold text-4xl md:text-5xl mb-4" style={{ color: colors.textColor }}>
                Contact Our Water Intelligence Team
            </h1>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: colors.lightText }}>
                Ready to transform your water management? Our team of experts is here to help you get started.
            </p>
        </div>
    );

    return (
        <Layout>
            <div className="relative flex flex-column min-h-screen">
                {/* Background elements */}
                <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10">
                    <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"></div>
                    <div className="absolute top-60 -left-40 w-80 h-80 bg-cyan-500/5 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-64 bg-gradient-to-t from-blue-500/10 to-transparent"></div>
                </div>

                {/* AppBar */}
                <AppBar scrolled={false} navItems={[
                    { id: 'faq', label: 'FAQ' },
                    {id: 'send-us-email', label:'Send Us Email'},
            
                ]} />

                {/* Main Content */}
                <div className="flex-grow-1 pt-16">
                    <div className="py-12 px-4 mx-0 md:mx-6 lg:mx-8 lg:px-8">
                        {/* Header */}
                        {headerTemplate()}

                        {/* Main Contact Section */}
                        <div id="send-us-email" className="grid mb-16">
                            {/* Contact Form */}
                            <div className="col-12 lg:col-7 p-2">
                                <Card className="h-full border-round-2xl shadow-3"
                                      style={{ 
                                          backgroundColor: colors.cardBg, 
                                          borderColor: colors.borderColor 
                                      }}>
                                    <div className="flex align-items-center gap-3 mb-6">
                                        <div className="p-3 border-round-lg" 
                                             style={{ 
                                                 backgroundColor: `${colors.primaryColor}15`,
                                                 color: colors.primaryColor 
                                             }}>
                                            <FaPaperPlane size={24} />
                                        </div>
                                        <h2 className="font-bold text-2xl m-0" style={{ color: colors.textColor }}>
                                            Send us a Message
                                        </h2>
                                    </div>

                                    <form onSubmit={handleSubmit} className="grid">
                                        {/* Name Row */}
                                        <div className="col-12 md:col-6 p-2">
                                            <div className="flex flex-column gap-2">
                                                <label className="font-medium" style={{ color: colors.textColor }}>
                                                    <FaUser className="mr-2" />
                                                    First Name *
                                                </label>
                                                <InputText
                                                    name="firstName"
                                                    value={formData.firstName}
                                                    onChange={handleInputChange}
                                                    placeholder="John"
                                                    required
                                                    className="w-full p-3 border-round-lg"
                                                    style={{ 
                                                        backgroundColor: colors.surfaceBg,
                                                        borderColor: colors.borderColor,
                                                        color: colors.textColor
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-12 md:col-6 p-2">
                                            <div className="flex flex-column gap-2">
                                                <label className="font-medium" style={{ color: colors.textColor }}>
                                                    Last Name *
                                                </label>
                                                <InputText
                                                    name="lastName"
                                                    value={formData.lastName}
                                                    onChange={handleInputChange}
                                                    placeholder="Doe"
                                                    required
                                                    className="w-full p-3 border-round-lg"
                                                    style={{ 
                                                        backgroundColor: colors.surfaceBg,
                                                        borderColor: colors.borderColor,
                                                        color: colors.textColor
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Contact Info */}
                                        <div className="col-12 md:col-6 p-2">
                                            <div className="flex flex-column gap-2">
                                                <label className="font-medium" style={{ color: colors.textColor }}>
                                                    <FaEnvelope className="mr-2" />
                                                    Email *
                                                </label>
                                                <InputText
                                                    name="email"
                                                    type="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    placeholder="john@company.com"
                                                    required
                                                    className="w-full p-3 border-round-lg"
                                                    style={{ 
                                                        backgroundColor: colors.surfaceBg,
                                                        borderColor: colors.borderColor,
                                                        color: colors.textColor
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-12 md:col-6 p-2">
                                            <div className="flex flex-column gap-2">
                                                <label className="font-medium" style={{ color: colors.textColor }}>
                                                    <FaPhone className="mr-2" />
                                                    Phone
                                                </label>
                                                <InputText
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleInputChange}
                                                    placeholder="+263 71 012 3456"
                                                    className="w-full p-3 border-round-lg"
                                                    style={{ 
                                                        backgroundColor: colors.surfaceBg,
                                                        borderColor: colors.borderColor,
                                                        color: colors.textColor
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Company Info */}
                                        <div className="col-12 md:col-6 p-2">
                                            <div className="flex flex-column gap-2">
                                                <label className="font-medium" style={{ color: colors.textColor }}>
                                                    <FaBuilding className="mr-2" />
                                                    Company
                                                </label>
                                                <InputText
                                                    name="company"
                                                    value={formData.company}
                                                    onChange={handleInputChange}
                                                    placeholder="Your Organization"
                                                    className="w-full p-3 border-round-lg"
                                                    style={{ 
                                                        backgroundColor: colors.surfaceBg,
                                                        borderColor: colors.borderColor,
                                                        color: colors.textColor
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-12 md:col-6 p-2">
                                            <div className="flex flex-column gap-2">
                                                <label className="font-medium" style={{ color: colors.textColor }}>
                                                    Job Title
                                                </label>
                                                <InputText
                                                    name="jobTitle"
                                                    value={formData.jobTitle}
                                                    onChange={handleInputChange}
                                                    placeholder="e.g., Water Manager"
                                                    className="w-full p-3 border-round-lg"
                                                    style={{ 
                                                        backgroundColor: colors.surfaceBg,
                                                        borderColor: colors.borderColor,
                                                        color: colors.textColor
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Inquiry Type */}
                                        <div className="col-12 p-2">
                                            <div className="flex flex-column gap-2">
                                                <label className="font-medium" style={{ color: colors.textColor }}>
                                                    <FaHeadset className="mr-2" />
                                                    What can we help you with? *
                                                </label>
                                                <Dropdown
                                                    name="inquiryType"
                                                    value={formData.inquiryType}
                                                    onChange={handleInputChange}
                                                    options={inquiryTypes}
                                                    optionLabel="label"
                                                    placeholder="Select an option"
                                                    className="w-full border-round-lg"
                                                    style={{ 
                                                        backgroundColor: colors.surfaceBg,
                                                        borderColor: colors.borderColor,
                                                        color: colors.textColor
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Message */}
                                        <div className="col-12 p-2">
                                            <div className="flex flex-column gap-2">
                                                <label className="font-medium" style={{ color: colors.textColor }}>
                                                    Your Message *
                                                </label>
                                                <InputTextarea
                                                    name="message"
                                                    value={formData.message}
                                                    onChange={handleInputChange}
                                                    placeholder="Tell us about your water management challenges..."
                                                    rows={5}
                                                    required
                                                    className="w-full p-3 border-round-lg"
                                                    style={{ 
                                                        backgroundColor: colors.surfaceBg,
                                                        borderColor: colors.borderColor,
                                                        color: colors.textColor,
                                                        resize: 'none'
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Consent */}
                                        <div className="col-12 p-2">
                                            <div className="flex align-items-start gap-3">
                                                <input
                                                    type="checkbox"
                                                    name="consent"
                                                    id="consent"
                                                    checked={formData.consent}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="mt-1"
                                                    style={{ 
                                                        accentColor: colors.primaryColor
                                                    }}
                                                />
                                                <label htmlFor="consent" className="text-sm" style={{ color: colors.lightText }}>
                                                    I consent to AmanziLytics collecting and storing my data from this form. 
                                                    I understand my data will be processed in accordance with the Privacy Policy.
                                                </label>
                                            </div>
                                        </div>

                                        {/* Submit Button */}
                                        <div className="col-12 p-2">
                                            <Button 
                                                type="submit"
                                                label="Send Message"
                                                icon="pi pi-send"
                                                loading={loading}
                                                className="w-full p-3 text-lg font-bold border-round-lg"
                                                style={{ 
                                                    backgroundColor: colors.primaryColor,
                                                    borderColor: colors.primaryColor
                                                }}
                                            />
                                        </div>
                                    </form>
                                </Card>
                            </div>

                            {/* Contact Information */}
                            <div className="col-12 lg:col-5 p-2">
                                <div className="flex flex-column gap-6">
                                    {/* Contact Cards */}
                                    <Card className="border-round-2xl shadow-3"
                                          style={{ 
                                              backgroundColor: colors.cardBg, 
                                              borderColor: colors.borderColor 
                                          }}>
                                        <h3 className="font-bold text-xl mb-6" style={{ color: colors.textColor }}>
                                            Contact Information
                                        </h3>
                                        <div className="grid">
                                            {contactInfo.map((info, index) => (
                                                <div key={index} className="col-12 md:col-6 p-2">
                                                    <div className="p-4 border-round-lg hover:shadow-2 transition-all duration-300 cursor-pointer"
                                                         style={{ 
                                                             backgroundColor: colors.surfaceBg,
                                                             border: `1px solid ${colors.borderColor}`
                                                         }}>
                                                        <div className="flex align-items-center gap-3 mb-3">
                                                            <div className={`pt-3 pr-0 border-round-lg ${info.color} text-white`}>
                                                                {info.icon}
                                                            </div>
                                                            <span className="pt-3 font-semibold" style={{ color: colors.textColor }}>
                                                                {info.title}
                                                            </span>
                                                        </div>
                                                        <div className="flex flex-column gap-1">
                                                            {info.details.map((detail, idx) => (
                                                                <span key={idx} className="text-sm" style={{ color: colors.lightText }}>
                                                                    {detail}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </Card>

    
                                </div>
                            </div>
                        </div>

                        {/* FAQ Section */}
                        <div id="faq" className="mb-16">
                            <div className="text-center mb-8">
                                <h2 className="font-bold text-3xl mb-4" style={{ color: colors.textColor }}>
                                    Frequently Asked Questions
                                </h2>
                                <p className="text-xl max-w-3xl mx-auto" style={{ color: colors.lightText }}>
                                    Find quick answers to common questions about our platform and services.
                                </p>
                            </div>
                            <div className="grid">
                                {faqs.map((faq, index) => (
                                    <div key={index} className="col-12 md:col-6 p-2">
                                        <Card className="h-full border-round-xl hover:shadow-3 transition-all duration-300"
                                              style={{ 
                                                  backgroundColor: colors.cardBg, 
                                                  borderColor: colors.borderColor 
                                              }}>
                                            <div className="flex align-items-start gap-3">
                                                <div className="p-2 border-round mt-1"
                                                     style={{ 
                                                         backgroundColor: colors.primaryBg,
                                                         color: colors.primaryColor
                                                     }}>
                                                    <span className="font-bold">Q</span>
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-lg mb-2" style={{ color: colors.textColor }}>
                                                        {faq.question}
                                                    </h4>
                                                    <p style={{ color: colors.lightText }}>
                                                        {faq.answer}
                                                    </p>
                                                </div>
                                            </div>
                                        </Card>
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            {/* Success Dialog */}
            <Dialog 
                visible={submitted} 
                onHide={() => setSubmitted(false)}
                className="border-round-2xl"
                style={{ 
                    backgroundColor: colors.cardBg,
                    borderColor: colors.borderColor
                }}
                header={
                    <div className="flex align-items-center gap-3">
                        <FaCheckCircle style={{ color: colors.successColor, fontSize: '1.5rem' }} />
                        <span className="font-bold text-xl" style={{ color: colors.textColor }}>
                            Message Sent Successfully!
                        </span>
                    </div>
                }
            >
                <div className="flex flex-column align-items-center gap-4 py-4">
                    <div className="text-6xl">🎉</div>
                    <div className="text-center">
                        <p className="text-lg mb-2" style={{ color: colors.textColor }}>
                            Thank you for contacting AmanziLytics!
                        </p>
                        <p style={{ color: colors.lightText }}>
                            Our team will get back to you within 24 hours. 
                            For urgent matters, please call our emergency support line.
                        </p>
                    </div>
                    <Button 
                        label="Close" 
                        className="font-bold px-6"
                        style={{ backgroundColor: colors.primaryColor }}
                        onClick={() => setSubmitted(false)}
                    />
                </div>
            </Dialog>
        </Layout>
    );
};

export default ContactUs;